'use client';

/**
 * Transient (session-only) Agency cart, held client-side and persisted to
 * `sessionStorage` so it survives navigation/reload within the session but not
 * a new session — conforming to "Panier transient, non persisté" (design D1).
 * Pure rules (1 panier = 1 DMC, duplicates as distinct lines) live in
 * `cart-state.ts`; this provider owns persistence + `lineId` generation.
 */

import { Alert, Snackbar } from '@mui/material';
import { useTranslations } from 'next-intl';
import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import {
    addLine,
    type AddLineResult,
    type CartLine,
    type CartLineDetails,
    type CartState,
    cartCount,
    cartDmcSlug,
    emptyCart,
    isCartEmpty,
    removeLine as removeLineFromState,
    updateLineDetails,
} from '@/configurations/cart/cart-state';

const STORAGE_KEY = 'catalog-cart-v1';

type CartContextValue = {
    lines: CartLine[];
    count: number;
    dmcSlug: string | null;
    isEmpty: boolean;
    /** Bumped on each successful add — the navbar dropdown auto-opens on change. */
    lastAddSignal: number;
    /** Add a line; generates its `lineId`. Returns the add result (for the toast / multi-DMC error). */
    addToCart: (line: Omit<CartLine, 'lineId'>) => AddLineResult;
    removeLine: (lineId: string) => void;
    updateDetails: (lineId: string, details: CartLineDetails) => void;
    clear: () => void;
};

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
    const t = useTranslations('cart');
    const [state, setState] = useState<CartState>(emptyCart);
    const [lastAddSignal, setLastAddSignal] = useState(0);
    const [toastOpen, setToastOpen] = useState(false);
    const hydrated = useRef(false);
    const stateRef = useRef(state);
    useLayoutEffect(() => {
        stateRef.current = state;
    });

    // Hydrate from sessionStorage after mount (avoids SSR/client mismatch).
    useEffect(() => {
        try {
            const raw = sessionStorage.getItem(STORAGE_KEY);
            if (raw) setState(JSON.parse(raw) as CartState);
        } catch {
            // Corrupted/unavailable storage → start from an empty cart.
        }
        hydrated.current = true;
    }, []);

    useEffect(() => {
        if (!hydrated.current) return;
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch {
            // Storage full/unavailable — the cart stays in-memory for the session.
        }
    }, [state]);

    const addToCart = useCallback((line: Omit<CartLine, 'lineId'>): AddLineResult => {
        const withId: CartLine = { ...line, lineId: crypto.randomUUID() };
        const result = addLine(stateRef.current, withId);
        if (result.ok) {
            // A second add can land before the re-render that would refresh the ref.
            stateRef.current = result.state;
            setState(result.state);
            setLastAddSignal((n) => n + 1);
            setToastOpen(true);
        }
        return result;
    }, []);

    const removeLine = useCallback((lineId: string) => {
        setState((prev) => removeLineFromState(prev, lineId));
    }, []);

    const updateDetails = useCallback((lineId: string, details: CartLineDetails) => {
        setState((prev) => updateLineDetails(prev, lineId, details));
    }, []);

    const clear = useCallback(() => {
        setState(emptyCart());
    }, []);

    const value = useMemo<CartContextValue>(
        () => ({
            lines: state.lines,
            count: cartCount(state),
            dmcSlug: cartDmcSlug(state),
            isEmpty: isCartEmpty(state),
            lastAddSignal,
            addToCart,
            removeLine,
            updateDetails,
            clear,
        }),
        [state, lastAddSignal, addToCart, removeLine, updateDetails, clear],
    );

    return (
        <CartContext.Provider value={value}>
            {children}
            <Snackbar
                open={toastOpen}
                autoHideDuration={3000}
                onClose={() => setToastOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    severity="success"
                    variant="filled"
                    onClose={() => setToastOpen(false)}
                    data-testid="cart-added-toast"
                >
                    {t('added')}
                </Alert>
            </Snackbar>
        </CartContext.Provider>
    );
}

export function useCart(): CartContextValue {
    const ctx = useContext(CartContext);
    if (!ctx) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return ctx;
}
